// ===================================
// TODO CLEAN - MAIN APP COMPONENT
// Root component with routing and global providers
// ===================================

import { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Import layout components
import Layout from './components/layout/Layout';
import Loading from './components/ui/Loading';
import ScrollToTop from './components/ScrollToTop';

// Import context providers
import { AppProvider } from './contexts/AppContext';
import { CalculatorProvider } from './contexts/CalculatorContext';
import { QuoteModalProvider } from './contexts/QuoteModalContext';

// Import modal components
import QuoteModal from './components/ui/QuoteModal';

// Import utilities
import { trackPageView } from './utils/analytics';
import { SITE_CONFIG, NAVIGATION_ROUTES } from './utils/constants';
import { WhatsAppIcon } from './components/icons/SocialIcons';

// ==========================================
// LAZY LOADED PAGES
// ==========================================

// Main pages
const HomePage = lazy(() => import('./pages/HomePage'));
const ServiciosPage = lazy(() => import('./pages/ServiciosPage'));
const ServiciosResidencialPage = lazy(() => import('./pages/ServiciosResidencialPage'));
const ServiciosEmpresarialPage = lazy(() => import('./pages/ServiciosEmpresarialPage'));
// const CotizadorPage = lazy(() => import('./pages/CotizadorPage'));
const CoberturaPage = lazy(() => import('./pages/CoberturaPage'));
const SobreNosotrosPage = lazy(() => import('./pages/SobreNosotrosPage'));
const ContactoPage = lazy(() => import('./pages/ContactoPage'));

// Legal pages
const PoliticaPrivacidadPage = lazy(() => import('./pages/PoliticaPrivacidadPage'));
const TerminosCondicionesPage = lazy(() => import('./pages/TerminosCondicionesPage'));

// Error pages
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// ==========================================
// ROUTE ANALYTICS TRACKER
// ==========================================

function RouteAnalytics() {
  const location = useLocation();

  useEffect(() => {
    // Track page views with analytics
    trackPageView(location.pathname, document.title);
    
    // Update page focus for screen readers
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }, [location.pathname]);

  return null;
}

// ==========================================
// LOADING FALLBACK COMPONENT
// ==========================================

function PageLoadingFallback() {
  return (
    <div className='min-h-screen flex items-center justify-center bg-neutral-50'>
      <div className='text-center'>
        <Loading size='lg' />
        <p className='mt-4 text-neutral-600 font-medium'>
          Cargando...
        </p>
      </div>
    </div>
  );
}

// ==========================================
// MAIN APP COMPONENT
// ==========================================

function App() {
  return (
    <AppProvider>
      <CalculatorProvider>
        <QuoteModalProvider>
        <div className='App'>
          {/* Global SEO */}
          <Helmet>
            <html lang='es' />
            <meta charSet='utf-8' />
            <meta name='viewport' content='width=device-width, initial-scale=1, shrink-to-fit=no' />
            <meta name='theme-color' content='#2563eb' />
            <link rel='canonical' href={SITE_CONFIG.url} />
            
            {/* Preload critical resources */}
            <link rel='preload' href='/fonts/inter-var.woff2' as='font' type='font/woff2' crossOrigin='anonymous' />
            
            {/* Preconnect to external domains for performance */}
            <link rel='preconnect' href='https://www.googletagmanager.com' />
            <link rel='preconnect' href='https://wa.me' />
            
            {/* DNS prefetch for external resources */}
            <link rel='dns-prefetch' href='//maps.googleapis.com' />
            <link rel='dns-prefetch' href='//fonts.googleapis.com' />
            <link rel='dns-prefetch' href='//fonts.gstatic.com' />
          </Helmet>

          {/* Route analytics tracker */}
          <RouteAnalytics />
          
          {/* Scroll to top on route changes */}
          <ScrollToTop />

          {/* Main application routes */}
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              {/* Main layout routes with Layout wrapper */}
              <Route element={<Layout />}>
                {/* Home page */}
                <Route path='/' element={<HomePage />} />
                
                {/* Services pages */}
                <Route path='servicios' element={<ServiciosPage />} />
                <Route path='servicios/residencial' element={<ServiciosResidencialPage />} />
                <Route path='servicios/empresarial' element={<ServiciosEmpresarialPage />} />
                
                {/* Calculator page - TEMPORARILY HIDDEN */}
                {/* <Route path='cotizador' element={<CotizadorPage />} /> */}
                
                {/* Coverage page */}
                <Route path='cobertura' element={<CoberturaPage />} />
                
                {/* About page */}
                <Route path='sobre-nosotros' element={<SobreNosotrosPage />} />
                
                {/* Contact page */}
                <Route path='contacto' element={<ContactoPage />} />
                
                {/* Legal pages */}
                <Route path='politica-privacidad' element={<PoliticaPrivacidadPage />} />
                <Route path='terminos-condiciones' element={<TerminosCondicionesPage />} />
              </Route>

              {/* 404 page - outside of layout for custom styling */}
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </Suspense>

          {/* WhatsApp floating button - only on specific routes */}
          <WhatsAppFloat />

          {/* Quote Modal - Global */}
          <QuoteModal />

          {/* Accessibility improvements */}
          <div id='announcements' className='sr-only' aria-live='polite' aria-atomic='true'></div>
        </div>
        </QuoteModalProvider>
      </CalculatorProvider>
    </AppProvider>
  );
}

// ==========================================
// WHATSAPP FLOATING BUTTON COMPONENT
// ==========================================

function WhatsAppFloat() {
  const location = useLocation();
  
  // Hide on certain pages where it might interfere
  const hiddenRoutes = ['/contacto', '/cotizador'];
  const shouldShow = !hiddenRoutes.includes(location.pathname);
  
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show after a delay to avoid interfering with page load
    const timer = setTimeout(() => {
      setIsVisible(shouldShow);
    }, 2000);

    return () => clearTimeout(timer);
  }, [shouldShow]);

  if (!isVisible) return null;

  const handleWhatsAppClick = () => {
    // Track WhatsApp click
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'floating_button',
        page_path: location.pathname,
      });
    }

    // Open WhatsApp
    const message = encodeURIComponent(
      '¡Hola! Me interesa conocer más sobre sus servicios de limpieza.'
    );
    window.open(
      `https://wa.me/${SITE_CONFIG.whatsapp}?text=${message}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <button
      onClick={handleWhatsAppClick}
      className='whatsapp-float btn-whatsapp rounded-full p-4 shadow-xl animate-pulse-whatsapp hover:animate-none transition-all duration-300'
      aria-label='Contactar por WhatsApp'
      title='¿Necesitas ayuda? Escríbenos por WhatsApp'
    >
      <WhatsAppIcon className='w-6 h-6' />
    </button>
  );
}

// ==========================================
// DEVELOPMENT HELPERS
// ==========================================

if (import.meta.env.DEV) {
  // Add route debugging information
  const routeInfo = NAVIGATION_ROUTES.map(route => ({
    name: route.name,
    path: route.path,
    component: `${route.name}Page`,
  }));
  
  console.log('🗺️ Available routes:', routeInfo);
}

export default App;